import { IS_MOBILE } from '@constants/global';
import { XIcon } from '@icons/index.js';
import { subscribe } from '@router/index.js';
import DrawerState from '@states/DrawerState';
import PreviewState from '@states/Preview';
import { UIButton, UIDiv, UIText } from '@ui';
import i18next from 'i18next';

export class BottomActionBar extends UIDiv {
  constructor({ selectedItems = [], actions = [], onClearSelection = null }) {
    super();

    this.selectedItems = selectedItems;
    this.actions = actions;
    this.onClearSelection = onClearSelection;

    this.setClass(
      'fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 flex items-center gap-4 z-50 hidden',
    );

    this.render();

    // Subscribe to router updates
    const handleStateChange = () => {
      this.hide();
      if (this.onClearSelection) {
        this.onClearSelection();
      }
    };

    subscribe(() => handleStateChange());
    PreviewState.subscribe(() => handleStateChange());
    DrawerState.subscribe(() => handleStateChange());
  }

  render() {
    this.clear();

    // Left section - Selection info and cancel button
    const leftSection = new UIDiv();
    leftSection.setClass('flex items-center gap-3');

    // Cancel/close button
    const cancelButton = new UIButton();
    cancelButton.setClass('p-1 hover:bg-gray-100 rounded-md transition-colors');
    const cancelIcon = new XIcon({
      size: 18,
      color: 'none',
      strokeColor: 'black',
    });
    cancelButton.add(cancelIcon);
    cancelButton.onClick(e => {
      e.preventDefault();
      this.hide();
      if (this.onClearSelection) {
        this.onClearSelection();
      }
    });

    // Selection count
    const selectionText = new UIText(
      i18next.t('pages.projects.bottomActionBar.selected', {
        count: this.selectedItems.length,
      }),
    );
    selectionText.setClass(
      'text-sm font-medium text-gray-700 whitespace-nowrap',
    );

    leftSection.add(cancelButton, selectionText);

    // Right section - Action buttons
    const actionsSection = new UIDiv();
    actionsSection.setClass('flex items-center gap-2');

    if (this.actions.length === 0) {
      const noActionsText = new UIText(i18next.t('pages.projects.no_actions'));
      noActionsText.setClass('text-sm text-gray-500');
      actionsSection.add(noActionsText);
    }

    this.actions.forEach(action => {
      const button = new UIButton();
      button.setClass(
        'flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors',
      );

      if (action.icon) {
        const icon = new action.icon({
          size: 16,
          color: 'none',
          strokeColor: 'black',
        });
        button.add(icon);
      }

      if (!IS_MOBILE()) {
        const label = new UIText(action.label);
        button.add(label);
      }

      button.onClick(async () => {
        button.setDisabled(true);
        try {
          await action.handler();
        } finally {
          this.hide();
        }
      });

      actionsSection.add(button);
    });

    this.add(leftSection, actionsSection);
  }

  updateSelection(selectedItems, actions = []) {
    this.selectedItems = selectedItems;
    this.actions = actions;
    this.render();

    if (selectedItems.length > 0) {
      this.show();
    } else {
      this.hide();
    }
  }

  show() {
    this.removeClass('hidden');
    this.addClass('flex');
  }

  hide() {
    this.addClass('hidden');
    this.removeClass('flex');
  }

  destroy() {
    this.hide();
    if (this.dom && this.dom.parentNode) {
      this.dom.parentNode.removeChild(this.dom);
    }
  }
}
